import React, { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPencil, faTimes } from "@fortawesome/pro-light-svg-icons";
import styled from "styled-components";
import { Cell } from "src/components/Table/Cell";
import TableCell from "src/components/Table/TableCell";
import TableRow from "src/components/Table/TableRow";
import { colors } from "src/styles";
import { Ticket } from "src/types";
import { useSelector } from "react-redux";
import { getAllActiveResources, getPriorityLevels } from "src/redux/selectors/resources";
import { getUserData } from "src/redux/selectors/user";
import { useNavigate } from "react-router-dom";
import { TablePriorityBadge, TableStatusDot, TableStatusInline } from "../styles";
import WorkRequestListContext from "src/pages/WorkRequests/context";
import { getResourseData } from "src/components/molecules/WorkRequestListViewContainer/handler";
import RecurringScheduleActionModal from "./RecurringScheduleActionModal";

const ManagerTableRow = ({ ticket }: { ticket: Ticket }) => {
  const navigate = useNavigate();
  const { searchText } = useContext(WorkRequestListContext);
  const { clientId } = useSelector(getUserData);
  const { employees, vendors } = useSelector(getAllActiveResources);
  const priorityLevels = useSelector(getPriorityLevels);

  const pickedPriorityLevel = priorityLevels?.[ticket.clientId ?? clientId]?.find(
    ({ priority }) => priority === ticket.priorityId,
  );
  const assignee =
    getResourseData(ticket.vendorId, ticket.laborId, vendors, employees) || ticket.assignedResourceName || "Unassigned";
  const statusRaw = ticket.statusDescription ?? ticket.status ?? "";
  const statusLabel = statusRaw.replace(/_/g, " ");
  const isNewStatus = statusRaw.trim().toLowerCase() === "new";
  const isRecurringScheduleRow = Boolean(ticket.recurringScheduleId);
  const hasLinkedWorkOrder = Boolean(ticket.linkedWonum);
  const [activeRecurringMode, setActiveRecurringMode] = useState<"edit" | "deactivate" | null>(null);
  const displayWonum = ticket.displayWonum || ticket.wonum;

  const splittedDate = ticket.dateCreated.split(", ");

  const notifyRefresh = () => {
    window.dispatchEvent(new CustomEvent("workrequests-refresh"));
  };

  const onRowClick = (wonum: string) => {
    if (isRecurringScheduleRow && !hasLinkedWorkOrder) {
      return;
    }
    navigate(`/workrequests/${wonum}`, { state: { ticketNumber: wonum } });
  };
  return (
    <TableRow
      onClick={() => {
        onRowClick && onRowClick(ticket.wonum);
      }}
      $appearance="legacy"
      $clickable={true}
      key={ticket.wonum}
    >
      <Cell appearance="legacy" showMark highlight={searchText ?? ""}>
        {displayWonum}
      </Cell>
      <Cell appearance="legacy" highlight={searchText ?? ""} bold={splittedDate[0] ?? ""}>
        {(searchText ?? ""?.trim().length) ? ticket.dateCreated : `, ${splittedDate[1] ?? ""}`}
      </Cell>

      <Cell appearance="legacy" showMark highlight={searchText ?? ""}>
        {ticket.client ?? ""}
      </Cell>

      <Cell appearance="legacy" showMark highlight={searchText ?? ""}>
        {ticket.location}
      </Cell>
      <Cell appearance="legacy" showMark highlight={searchText ?? ""}>
        {ticket.description ?? ""}
      </Cell>

      <TableCell $appearance="legacy">
        <TablePriorityBadge color={pickedPriorityLevel ? pickedPriorityLevel.color : colors.grey.default}>
          {ticket.priority}
        </TablePriorityBadge>
      </TableCell>
      <TableCell $appearance="legacy">
        <TableStatusInline>
          <TableStatusDot $isNew={isNewStatus} />
          {statusLabel}
        </TableStatusInline>
      </TableCell>
      <TableCell $appearance="legacy">
        <div>{assignee}</div>
        {isRecurringScheduleRow && (
          <ActionButtonsRow onClick={(e) => e.stopPropagation()}>
            <ActionBtn
              type="button"
              aria-label="Monitor recurring schedule"
              title="Monitor"
              onClick={() =>
                navigate(`/workrequests/recurring-schedules/${ticket.recurringScheduleId}`, {
                  state: { ticket },
                })
              }
            >
              <FontAwesomeIcon icon={faEye} />
            </ActionBtn>
            <ActionBtn
              type="button"
              aria-label="Edit recurring schedule"
              title="Edit"
              onClick={() => setActiveRecurringMode("edit")}
            >
              <FontAwesomeIcon icon={faPencil} />
            </ActionBtn>
            <DangerActionBtn
              type="button"
              aria-label="Deactivate recurring schedule"
              title="Deactivate"
              onClick={() => setActiveRecurringMode("deactivate")}
            >
              <FontAwesomeIcon icon={faTimes} />
            </DangerActionBtn>
          </ActionButtonsRow>
        )}
      </TableCell>
      {isRecurringScheduleRow && activeRecurringMode && (
        <RecurringScheduleActionModal
          isOpen={true}
          mode={activeRecurringMode}
          scheduleId={ticket.recurringScheduleId}
          ticket={ticket}
          onClose={() => setActiveRecurringMode(null)}
          onCompleted={notifyRefresh}
        />
      )}
    </TableRow>
  );
};

const ActionButtonsRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;
`;

const ActionBtn = styled.button`
  border: 1px solid #1f2a37;
  background: #ffffff;
  color: #1f2a37;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 13px;
  cursor: pointer;
`;

const DangerActionBtn = styled(ActionBtn)`
  border-color: ${colors.red.default};
  color: ${colors.red.default};
`;

export default ManagerTableRow;
